import React, { useEffect, useState } from "react";
import { EntityHistory } from "@/components/common/EntityHistory";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { HistoryItem } from "@/hooks/useHistoryOperations";

interface ListHistoryTabProps {
	listId: string;
}

export const ListHistoryTab = ({ listId }: ListHistoryTabProps) => {
	const { t } = useTranslation();
	const [history, setHistory] = useState<HistoryItem[]>([]);
	const [loading, setLoading] = useState(false);

	// Fetch both list changes and membership changes
	useEffect(() => {
		const fetchHistory = async () => {
			if (!listId) return;

			try {
				setLoading(true);

				// Fetch list changes (name, description, etc.)
				const { data: listHistory, error: listError } = await supabase
					.from('history_lists')
					.select('*')
					.eq('list_id', listId)
					.order('changed_at', { ascending: false });

				if (listError) throw listError;

				// Fetch membership changes (candidates added/removed)
				const { data: memberHistory, error: memberError } = await supabase
					.from('history_list_members')
					.select('*')
					.eq('list_id', listId)
					.order('changed_at', { ascending: false });

				if (memberError) throw memberError;

				// Combine both histories
				const allHistory: HistoryItem[] = [
					...(listHistory || []).map((item: any) => ({
						id: item.id,
						operation: item.operation || 'UPDATE',
						changed_fields: item.changed_fields || [],
						old_value: item.old_value,
						new_value: item.new_value,
						changed_by: item.changed_by || null,
						changed_at: item.changed_at,
						description: item.description,
						performed_by: item.performed_by || item.changed_by
					})),
					...(memberHistory || []).map((item: any) => ({
						id: item.id,
						operation: item.operation || 'UPDATE',
						changed_fields: ['member'], // Virtual field to indicate membership change
						old_value: item.old_value,
						new_value: item.new_value,
						changed_by: item.changed_by || null,
						changed_at: item.changed_at,
						description: item.operation === 'INSERT' ? 'Candidate added to list' : 
									 item.operation === 'DELETE' ? 'Candidate removed from list' : 
									 'Member updated',
						performed_by: item.changed_by
					}))
				];

				// Sort by date descending
				allHistory.sort((a, b) => 
					new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
				);

				setHistory(allHistory);
			} catch (error) {
				console.error('Error fetching list history:', error);
				setHistory([]);
			} finally {
				setLoading(false);
			}
		};

		fetchHistory();
	}, [listId]);

	return (
		<div>
			<h3 className="text-lg font-semibold mb-4">{t("lists.history.title", "List History")}</h3>
			<EntityHistory history={history} loading={loading} />
		</div>
	);
};
